import { RangeSliderValue } from "@shopify/polaris/build/ts/src/components/RangeSlider/types";
import {
    WidgetPositionsTypes,
    DiscountTypes,
    FontStylesTypes,
    GeneralSettingObj,
    WidgetShowOptionsTypes,
    BbOfferCtaPositionsType,
    BundleVisibilityTypes,
} from "./constants";
import { SwatchesProductsMap } from "../../components/SwatchesModal/SwatchesSelector";

export type ProductSourceType =
    | { type: "specific"; value: ProductObj[]; additionalProducts?: ProductObj[] }
    | { type: "collection"; value: string[]; additionalProducts?: ProductObj[] }
    | { type: "all"; value: []; additionalProducts?: ProductObj[] }
    | { type: "exclude"; value: ProductObj[]; additionalProducts?: ProductObj[] }
    | {
          type: "multi";
          value: {
              selectionType: "specific" | "collection";
              products?: ProductObj[];
              collectionIds?: string[];
          }[];
          additionalProducts?: ProductObj[];
      };

export interface ImageItem {
    originalSource: string;
    alt: string;
    mediaContentType: "IMAGE";
}
export interface VariantsObj {
    free?: boolean;
    id: string;
    displayName: string;
    title: string;
    price: string;
    image?: any;
    quantity?: number;
    selectedOptions?: any[];
    selling_plan?: string;
    copyPrice?: string;
    quantityAvailable?: number;
    availableForSale?: boolean;
    compareAtPrice: string;
    weight: number;
    weight_unit: string;
    qty?: number;
    sku?: string;
    sellingPlanIds?: string[];
    dosage_daily_dose?: string;
    dosage_no_of_days?: string;
    dosage_total_needed?: string;
}

export type SwatchOption = {
    name: string;
    /**
     * Locale-independent gid://shopify/ProductOption/... ids of every product sharing this option —
     * `name` is translated per market, and each product has its own option id.
     */
    optionIds?: string[];
    swatchType: string;
    colors: {
        name: string;
        color: string;
        products?: {
            productId: string;
            color: string;
        }[];
    }[];
    images: {
        name: string;
        image: string;
        products?: {
            productId: string;
            image: string;
        }[];
    }[];
};

export interface ProductObj {
    id: string;
    images: { altText: string; id: string; originalSrc: string }[];
    title: string;
    handle: string;
    quantity: number;
    variants: VariantsObj[];
    maxProductStock: number;
    selectedVariants: VariantsObj[];
    metafield?: {
        value: string;
        type: "json";
    };
    options?: any[];
    checked?: boolean; //for FBT
    required_product?: boolean; //for FBT
    subscriptions: {
        id: string;
        name: string;
        checked: boolean;
        discountType: DiscountTypes;
        discountValue: number;
        recurringDeliveries?: boolean;
        billingPolicy?: {
            __typename: string;
            interval: string;
            intervalCount: number;
        };
        deliveryPolicy?: {
            __typename: string;
            interval: string;
            intervalCount: number;
        };
        total_cycles?: string;
    }[];
    oneTimePurchase: boolean;
    isSubscribed: boolean;
    discountType?: any;
    cartUpsellId?: any;
    discountValue?: any;
    page?: string;
    subStepIdx?: number;
    alternateProducts?: {
        id: string;
        title: string;
        images: { altText: string; id: string; originalSrc: string }[];
        handle?: string;
        variants?: VariantsObj[];
    }[];
    parentName?: string;
    originalOptions?: any[];
    tags?: string[];
    isAddon?: boolean;
    parentProduct?: string;
    subStep?: string;
    isPreselected?: boolean;
    freeGift?: boolean;
    custom_text?: string;
    productProperties?: any;
    exclude_from_step_conditions?: boolean;
    upsell_text?: string;
    visible_when_bar_selected?: boolean; // using in upsell volume
    variantsCount?: {
        count: number;
        precision: number;
    };
}

export interface CollectionsType {
    id: string;
    image?: {
        originalSrc?: string;
    };
    title: string;
}

export interface DefaultVariantType {
    productID: string;
    variantIDs: (string | null)[];
}

export interface SubStepType {
    step_label: string;
    product_selection: "specific" | "collection";
    products: ProductObj[];
    collections: CollectionsType[];
    subStep_condition_basis: "items_count" | "bundle_total";
    subStep_condition_type: "max_products" | "min_products" | "exact_products";
    product_count_limit: number;
    step_error_msg: string;
    exempt_product_ids?: string[];
    allow_product_custom_text?: boolean;
    product_custom_text_label?: string;
    product_custom_text_placeholder?: string;
    hidden?: boolean;
}

export interface StepConditionType {
    basis: "items_count" | "bundle_total";
    type: "max_products" | "min_products" | "exact_products";
    limit: number;
    msg: string;
}

export interface ProductConditions {
    included_products: ProductObj[];
    hidden_products: ProductObj[];
    hidden_steps: string[];
    hidden_substeps: string[];
    action_type: "products" | "steps";
}

export interface FieldsType {
    field_type: "input" | "textarea" | "dropdown" | "file_upload";
    field_title: string;
    field_placeholder: string;
    is_required: boolean;
    dropdown_options?: string[];
    default_value?: string;
    file_upload_settings?: {
        max_file_size: number;
        allow_multiple: boolean;
        allowed_file_types: ("images" | "documents")[];
    };
}

export interface StepsType {
    step_label: string;
    page_title?: string;
    page_subtitle?: string;
    header_subtitle?: string;
    bottom_subtitle?: string;
    step_icon?: { originalSrc: string; alt: string } | null;
    step_content_type: "fields" | "products" | "free_gift";
    product_selection: "specific" | "collection";
    enable_subSteps: boolean;
    subSteps: SubStepType[];
    products: ProductObj[];
    collections: CollectionsType[];

    step_conditions: StepConditionType[];
    fields: FieldsType[];
    render_as_variants: boolean;
    show_step_conditions: boolean;
    show_banner: boolean;
    banner_desktop: string;
    banner_mobile: string;
    substep_rules_type: "no_rules" | "step_level" | "substep_level";
    allow_product_custom_text?: boolean;
    product_custom_text_label?: string;
    product_custom_text_placeholder?: string;
    exempt_product_ids?: string[];
    enable_dosage_calculation?: boolean;
    enable_capacity_progress_bar?: boolean;
    merge_total_capacity?: boolean;
}

export interface ABTestingConfig<T> {
    enable: 0 | 1;
    exp_id: string;
    variants: ABVariant<T>[];
    traffic_allocation_type: "custom" | "even";
    traffic_percentage: Record<string, number>;
}

export interface ABVariant<T> {
    id: string;
    status: "active" | "draft";
    overrides: Partial<T>;
}

export interface PlaceholderStepsType {
    step_label: string;
    page_title?: string;
    page_subtitle?: string;
    step_icon?: { originalSrc: string; alt: string } | null;
    step_content_type: "fields" | "products" | "free_gift";
    product_selection: "specific" | "collection";
    enable_subSteps?: boolean;
    subSteps?: SubStepType[];
    products: ProductObj[];
    collections: CollectionsType[];
    step_conditions: StepConditionType[];
    render_as_variants: boolean;
    show_step_conditions: boolean;
    show_banner: boolean;
    banner_desktop: string;
    banner_mobile: string;
    step_condition_type?: "max_products" | "min_products" | "exact_products";
    product_count_limit?: number;
    step_error_msg?: string;
}
export interface ReviewStepType {
    step_label: string;
    page_title?: string;
    is_enabled: boolean;
    step_icon?: { originalSrc: string; alt: string } | null;
    enable_addon_products?: boolean;
    addon_section_title: string;
    addon_product_discount_label: string;
    addon_button_text: string;
    addon_products_total_lable: string;
    addon_product_selection: "specific" | "collection";
    addon_products: ProductObj[];
    collections: CollectionsType[];
    apply_discount_on_addon_products?: boolean;
    discount_type?: DiscountTypes;
    discount_value?: number | null;
}

export interface FreeGiftStepType {
    is_enabled: boolean;
    step_label: string;
    page_title?: string;
    page_subtitle?: string;
    step_icon?: { originalSrc: string; alt: string } | null;
    products: ProductObj[];
    step_content_type: "products";
    product_selection: "specific" | "collection";
    collections: CollectionsType[];
    enable_subSteps: boolean;
    render_as_variants: boolean;
    show_banner: boolean;
    banner_desktop: string;
    banner_mobile: string;
    step_condition_type: "max_products" | "min_products" | "exact_products";
    product_count_limit: number;
    step_error_msg: string;
    gift_step?: boolean;
}

export interface TargetingConfig {
    includeCustomers: "all" | "tag" | "lifetimeSpent";
    excludeCustomers: "none" | "tag" | "lifetimeSpent";
    includeCustomerData: string;
    excludeCustomerData: string;
    shopifyMarketsTargeting: "all" | "targeted";
    shopifyMarketsTargetingData: string[];
    includeLocations: string[];
    excludeLocations: string[];

    // NewKeys start for Targeting
    targetingType: "all" | "specific" | string;
    customerTagsEnabled: boolean;
    customerTagType: "include" | "exclude" | string;
    customerTags: string;
    customerSpentEnabled: boolean;
    customerSpentType: "less_than" | "greater_than" | string;
    customerSpent: string;
    customerCountriesEnabled: boolean;
    customerCountryType: "include" | "exclude" | string;
    customerCountries: string[];
}

// ---------------------------------------Standard Bundle Config-----------
export interface BundleTypeSettings {
    enable_stock_control: boolean;
    out_of_stock_text: string;
    listing_variant_with_image: boolean;
    work_only_with_widget: boolean;
    bundle_position: WidgetPositionsTypes;
    custom_selector: string;
    custom_css: string;
    custom_js: string;
}

export interface StandardConfigInterface {
    status: boolean;
    bundle_type?: string;
    is_paused?: boolean;
    form_id?: string;
    ai_bundle_status?: boolean;
    bundle_as_product: {
        product_title: string;
        product_description: string;
        show_on_product_page: string[];
        images: {
            id: string;
            originalSource: string;
            alt: string;
            mediaContentType?: "IMAGE";
        }[];
        product_handle: string;
        variant_id?: string;
        double_discount: boolean;
        collections: {
            id: string;
            image?: {
                originalSrc?: string;
            };
            title: string;
        }[];
    };
    render_embed_on_visibility?: boolean;
    allowed_product_pages: string[] | [];
    render_product_pages_type: boolean;
    isProductPageVisible: boolean;
    show_on_no_pages?: boolean;
    // design form
    bundles_design: {
        default_cta: boolean;
        bundle_title_size: number;
        bundle_title_color: string;
        bundle_title_style: FontStylesTypes;
        bundle_widget_bg: string;
        bundle_widget_font_family: string;
        product_name_size: number;
        product_name_color: string;
        product_name_style: FontStylesTypes;
        product_price_size: number;
        product_price_color: string;
        product_price_style: FontStylesTypes;
        product_img_size?: string;
        product_card_seperator_color?: string;
        product_card_seperator_width?: number;
        product_img_show?: string;
        product_img_border_radius?: number;
        product_img_border_width?: number;
        product_img_border_color?: string;
        border_color: string;
        border_width: number;
        border_radius: number;
        bundle_border_width: number;
        bundle_border_color: string;

        background_color: string;
        highlighted_offer_tag_size: number;
        highlighted_offer_tag_color: string;
        highlighted_offer_tag_style: FontStylesTypes;
        highlighted_price_tag_size: number;
        highlighted_price_tag_color: string;
        highlighted_price_tag_style: FontStylesTypes;
        total_footer_text_size: number;
        total_footer_text_color: string;
        total_footer_text_style: FontStylesTypes;
        total_footer_price_text_size: number;
        total_footer_price_text_color: string;
        total_footer_price_text_style: FontStylesTypes;
        total_footer_background_color: string;
        total_footer_background_style: FontStylesTypes;
        product_background_color: string;
        product_border_color: string;
        variant_selector_text_size: number;
        variant_selector_text_color: string;
        variant_selector_text_style: FontStylesTypes;
        add_to_cart_bg_color: string;
        add_to_cart_border_radius: number;
        add_to_cart_border_color: string;
        add_to_cart_border_width: number;
        add_to_cart_text_color: string;
        add_to_cart_text_size: number;
        add_to_cart_text_style: FontStylesTypes;
        free_gift_header_bg_color: string;
        free_gift_header_color: string;
        free_gift_header_size: number;
        free_gift_header_style: FontStylesTypes;
        free_gift_bg_color: string;
        free_gift_text_color: string;
        free_gift_text_size: number;
        free_gift_text_style: FontStylesTypes;
        free_gift_border: boolean;
        free_gift_border_width: number;
        free_gift_border_color: string;
        volume_product_selection?: string;
        swatches_select_color: string;
    };

    // content form
    bundle_name: string;
    title: string;
    description: string;
    discount_type: DiscountTypes;
    discount_value: number | null;
    products: ProductObj[];
    offered_products?: ProductObj[];
    volume_discounts: any[];
    discounted_products: ProductObj[];
    bundle_product_level: "product" | "variant";
    hide_native_elements: string;
    add_to_cart_cta_text: string;
    quantity_text: string;
    footer_total_text: string;
    footer_text: string;
    free_text: string;
    no_stock_text: string;
    order_discounts: boolean;
    product_discounts: boolean;
    shipping_discounts: boolean;
    start_date: string;
    end_date: string | null;
    use_compare_at_price: boolean;
    allow_free_gifts: boolean;
    free_gifts: [];
    free_gift_text: string;
    selected_template_standard: string;
    bundle_embed_id: string;
    show_swatches: boolean;
    swatchOptions: SwatchOption[];
    swatchesProductsMap: SwatchesProductsMap;
    swatchShape: "circle" | "rounded" | "square";
    swatchSize: number;
    apply_discount_on_compare_price: boolean;
    disable_product_title_link?: boolean;
    offer_name: string;
    discount_label_template: string;
    show_unit_price: boolean;
    show_unit_price_text: string;

    // target form
    targeting: TargetingConfig;

    // Settings
    general_settings?: GeneralSettingObj;
    bundles_settings?: BundleTypeSettings;
    show_bundle_widget: WidgetShowOptionsTypes;
    free_shipping: boolean;
    animation_add_to_cart_btn: string;
    product_bundle_id?: string;
    max_width?: number;
    SwatchesBundleConfigTouchedRef?: any; // Use only for admin side, we dont save this key  on BE.
}

export interface ThankYouPageConfigInterface {
    status: boolean;
    upSell_id?: string;
    is_paused?: boolean;
    // Content configuration
    offer_name: string;
    upsell_type: "AI_RECOMMENDED" | "MANUAL";
    max_products_to_show: number;
    upsell_products: ProductObj[];
    has_discount: boolean;
    discount_type: "percentage" | "fixed" | "none";
    discount_value?: number;
    countdown_minutes: number;
    countdown_hours: number;
    timer_text: string;
    show_compare_price_strike: boolean;

    order_discounts: boolean;
    product_discounts: boolean;
    shipping_discounts: boolean;

    enable_free_shipping: boolean;

    // Trigger configuration
    trigger_type: "all" | "specific" | "exclude";
    trigger_products?: ProductObj[]; // Product IDs for specific/exclude products
    cart_trigger?: {
        trigger_type: "CART_TOTAL" | "CART_ITEMS";
        trigger_operator: "GREATER_THAN" | "LESS_THAN" | "EQUAL_TO";
        trigger_value: number;
        trigger_status: boolean; // To track if cart trigger is enabled
    };

    // Design configuration
    design: {
        // Timer configuration
        banner_status: "info" | "success" | "warning" | "critical";
        banner_text_size: "extraSmall" | "small" | "medium" | "large" | "extraLarge";
        banner_text_emphasis: "bold";
        timer_appearance: "critical" | "warning" | "info" | "success" | "accent" | "subdued";
        banner_text_color: "critical" | "warning" | "info" | "success" | "accent" | "subdued";

        // Product title styling
        product_title_appearance: "accent" | "info" | "success" | "warning" | "critical" | "subdued";
        product_title_size: "extraSmall" | "small" | "medium" | "large" | "extraLarge";
        product_title_emphasis?: "bold" | "italic";

        // Current price styling
        current_price_appearance: "accent" | "info" | "success" | "warning" | "critical" | "subdued";
        current_price_size: "extraSmall" | "small" | "medium" | "large" | "extraLarge";
        current_price_emphasis?: "bold" | "italic";

        // Compare at price styling
        compare_price_appearance: "accent" | "info" | "success" | "warning" | "critical" | "subdued";
        compare_price_size: "extraSmall" | "small" | "medium" | "large" | "extraLarge";

        // Discount badge styling
        discount_badge_appearance: "accent" | "info" | "success" | "warning" | "critical" | "subdued";
        discount_badge_size: "extraSmall" | "small" | "medium" | "large" | "extraLarge";
        discount_badge_emphasis?: "bold" | "italic";

        // Variant title styling
        variant_title_appearance: "accent" | "info" | "success" | "warning" | "critical" | "subdued";
        variant_title_size: "extraSmall" | "small" | "medium" | "large" | "extraLarge";

        // Stock status styling
        stock_status_size: "extraSmall" | "small" | "medium" | "large" | "extraLarge";
        in_stock_appearance: "accent" | "info" | "success" | "warning" | "critical" | "subdued";
        out_of_stock_appearance: "accent" | "info" | "success" | "warning" | "critical" | "subdued";

        // Error message styling
        show_quantity_selector: boolean;
        quantity_error_size: "extraSmall" | "small" | "medium";

        // Button styling
        button_text: string;
        price_note: string;
        button_appearance: "monochrome" | "critical" | "default";
        button_text_size: "extraSmall" | "small" | "medium" | "large" | "extraLarge";
        button_kind: "primary" | "secondary" | "plain";
    };
}

export interface ProductAddonsConfigInterface {
    status: boolean;
    bundle_type?: string;
    is_paused?: boolean;
    form_id?: string;
    ai_bundle_status?: boolean;
    bundle_as_product: {
        product_title: string;
        product_description: string;
        show_on_product_page: string[];
        images: {
            id: string;
            originalSource: string;
            alt: string;
            mediaContentType?: "IMAGE";
        }[];
        product_handle: string;
        variant_id?: string;
        double_discount: boolean;
        collections: {
            id: string;
            image?: {
                originalSrc?: string;
            };
            title: string;
        }[];
    };
    collections: CollectionsType[];
    render_embed_on_visibility: boolean;
    hide_native_elements: string;
    is_cta_above: boolean;
    allowed_product_pages: string[] | [];
    render_product_pages_type: boolean;
    isProductPageVisible: boolean;
    // design form
    bundles_design: {
        default_cta: boolean;
        bundle_title_size: number;
        bundle_title_color: string;
        bundle_title_style: FontStylesTypes;
        bundle_widget_bg: string;
        bundle_widget_font_family: string;
        product_name_size: number;
        product_name_color: string;
        product_name_style: FontStylesTypes;
        product_price_size: number;
        product_price_color: string;
        product_price_style: FontStylesTypes;
        variant_selector_text_size: number;
        variant_selector_text_color: string;
        variant_selector_text_style: FontStylesTypes;
        product_img_size?: string;
        product_img_show?: string;
        product_img_border_radius?: number;
        product_img_border_width?: number;
        product_img_border_color?: string;
        border_color: string;
        border_width: number;
        border_radius: number;
        background_color: string;
        highlighted_offer_tag_size: number;
        highlighted_offer_tag_color: string;
        highlighted_offer_tag_style: FontStylesTypes;
        highlighted_price_tag_size: number;
        highlighted_price_tag_color: string;
        highlighted_price_tag_style: FontStylesTypes;
        total_footer_text_size: number;
        total_footer_text_color: string;
        total_footer_text_style: FontStylesTypes;
        total_footer_price_text_size: number;
        total_footer_price_text_color: string;
        total_footer_price_text_style: FontStylesTypes;
        total_footer_background_color: string;
        total_footer_background_style: FontStylesTypes;
        product_background_color: string;
        product_border_color: string;
        add_to_cart_bg_color: string;
        add_to_cart_border_radius: number;
        add_to_cart_border_color: string;
        add_to_cart_border_width: number;
        add_to_cart_text_color: string;
        add_to_cart_text_size: number;
        add_to_cart_text_style: FontStylesTypes;
        swatches_select_color: string;
    };

    // content form
    product_add_on_selection: "all" | "manual" | "collection";
    fbt_show_checkboxes: 0 | 1;
    bundle_name: string;
    title: string;
    description: string;
    discount_type: DiscountTypes;
    discount_value: number | null;
    apply_discount_on_individual_products: boolean;
    individual_product_discounts?: {
        [productId: string]: {
            discount_type: DiscountTypes;
            discount_value: number;
        };
    };
    discount_label_template?: string;
    products: ProductObj[];
    offered_products: ProductObj[];
    volume_discounts: any[];
    discounted_products: ProductObj[];
    bundle_product_level: "product" | "variant";
    add_to_cart_cta_text: string;
    free_text: string;
    option_off_text?: string;
    order_discounts: boolean;
    product_discounts: boolean;
    shipping_discounts: boolean;
    start_date: string;
    end_date: string | null;
    use_compare_at_price: boolean;
    allow_free_gifts: boolean;
    free_gifts: [];
    free_gift_text: string;
    selected_template_product_add_on: string;
    bundle_embed_id: string;
    show_swatches: boolean;
    swatchOptions: SwatchOption[];
    swatchesProductsMap: SwatchesProductsMap;
    swatchShape: "circle" | "rounded" | "square";
    swatchSize: number;
    no_preselect_offered_products: boolean;
    apply_discount_on_compare_price: boolean;
    allow_radio_selection: boolean;
    remove_addons_if_main_missing: boolean;
    offer_name: string;
    same_addon_for_all_variants?: boolean;
    variant_addons?: {
        [variantId: string]: ProductObj[];
    };
    no_stock_text: string;
    main_product_out_of_stock_text: string;
    hide_out_of_stock_products: boolean;
    show_unit_price: boolean;
    show_unit_price_text: string;
    bundle_layout: "stacked" | "carousel";

    // target form
    targeting: TargetingConfig;

    // Settings
    general_settings?: GeneralSettingObj;
    bundles_settings?: BundleTypeSettings;
    show_bundle_widget: WidgetShowOptionsTypes;
    free_shipping: boolean;
    animation_add_to_cart_btn: string;
    product_bundle_id?: string;
    max_width?: number;
    SwatchesBundleConfigTouchedRef?: any;
}

export interface ProductAddonsPopupConfigInterface {
    status: boolean;
    bundle_type?: string;
    is_paused?: boolean;
    form_id?: string;
    ai_bundle_status?: boolean;
    collections: CollectionsType[];
    render_embed_on_visibility?: boolean;
    allowed_product_pages: string[] | [];
    render_product_pages_type: boolean;
    // isProductPageVisible: boolean;
    // design form
    bundles_design: {
        default_cta: boolean;
        bundle_title_size: number;
        bundle_title_color: string;
        bundle_title_style: FontStylesTypes;

        popup_heading_size: number;
        popup_heading_color: string;
        popup_heading_style: FontStylesTypes;
        popup_heading_bg_color: string;

        bundle_widget_bg: string;
        bundle_widget_font_family: string;
        product_name_size: number;
        product_name_color: string;
        product_name_style: FontStylesTypes;
        product_price_size: number;
        product_price_color: string;
        product_price_style: FontStylesTypes;
        variant_selector_text_size: number;
        variant_selector_text_color: string;
        variant_selector_text_style: FontStylesTypes;
        product_img_size?: string;
        product_img_show?: string;
        product_img_border_radius?: number;
        product_img_border_width?: number;
        product_img_border_color?: string;
        border_color: string;
        border_width: number;
        border_radius: number;
        background_color: string;
        highlighted_offer_tag_size: number;
        highlighted_offer_tag_color: string;
        highlighted_offer_tag_style: FontStylesTypes;
        highlighted_price_tag_size: number;
        highlighted_price_tag_color: string;
        highlighted_price_tag_style: FontStylesTypes;
        total_footer_text_size: number;
        total_footer_text_color: string;
        total_footer_text_style: FontStylesTypes;
        total_footer_price_text_size: number;
        total_footer_price_text_color: string;
        total_footer_price_text_style: FontStylesTypes;
        total_footer_background_color: string;
        total_footer_background_style: FontStylesTypes;
        product_background_color: string;
        product_border_color: string;
        add_to_cart_bg_color: string;
        add_to_cart_border_radius: number;
        add_to_cart_border_color: string;
        add_to_cart_border_width: number;
        add_to_cart_text_color: string;
        add_to_cart_text_size: number;
        add_to_cart_text_style: FontStylesTypes;

        skip_offer_bg_color: string;
        skip_offer_border_radius: number;
        skip_offer_text_color: string;
        skip_offer_text_size: number;
        skip_offer_text_style: FontStylesTypes;
        swatches_select_color: string;
    };

    // content form
    product_add_on_selection: "all" | "manual" | "collection";
    product_add_on_offered_products_type: "recommendations" | "specific_products";
    max_limit_of_recommendations: number;
    fbt_show_checkboxes: 0 | 1;
    bundle_name: string;
    title: string;
    popup_heading: string;
    description: string;
    discount_type: DiscountTypes;
    discount_value: number | null;
    products: ProductObj[];
    offered_products: ProductObj[];
    volume_discounts: any[];
    discounted_products: ProductObj[];
    bundle_product_level: "product" | "variant";
    add_to_cart_cta_text: string;
    skip_offer_cta_text: string;
    free_text: string;
    order_discounts: boolean;
    product_discounts: boolean;
    shipping_discounts: boolean;
    start_date: string;
    end_date: string | null;
    use_compare_at_price: boolean;
    bundle_embed_id: string;
    show_swatches: boolean;
    swatchOptions: SwatchOption[];
    swatchesProductsMap: SwatchesProductsMap;
    swatchShape: "circle" | "rounded" | "square";
    swatchSize: number;
    apply_discount_on_compare_price: boolean;
    offer_name: string;

    // target form
    targeting: TargetingConfig;

    // Settings
    general_settings?: GeneralSettingObj;
    bundles_settings?: BundleTypeSettings;
    show_bundle_widget: WidgetShowOptionsTypes;
    free_shipping: boolean;
    animation_add_to_cart_btn: string;
    product_bundle_id?: string;
    max_width?: number;
    SwatchesBundleConfigTouchedRef?: any;
}

export interface FBTConfigInterface {
    status: boolean;
    bundle_type?: string;
    is_paused?: boolean;
    form_id?: string;
    ai_bundle_status?: boolean;
    bundle_as_product: {
        product_title: string;
        product_description: string;
        show_on_product_page: string[];
        images: {
            id: string;
            originalSource: string;
            alt: string;
            mediaContentType?: "IMAGE";
        }[];
        product_handle: string;
        variant_id?: string;
        double_discount: boolean;
        collections: {
            id: string;
            image?: {
                originalSrc?: string;
            };
            title: string;
        }[];
    };
    allowed_product_pages: string[] | [];
    render_product_pages_type: boolean;
    hide_native_elements: string;
    isProductPageVisible: boolean;
    // design form
    bundles_design: {
        default_cta: boolean;
        bundle_title_size: number;
        bundle_title_color: string;
        bundle_title_style: FontStylesTypes;
        bundle_widget_bg: string;
        bundle_widget_font_family: string;
        product_name_size: number;
        product_name_color: string;
        product_name_style: FontStylesTypes;
        product_price_size: number;
        product_price_color: string;
        product_price_style: FontStylesTypes;
        variant_selector_text_size: number;
        variant_selector_text_color: string;
        variant_selector_text_style: FontStylesTypes;
        product_img_size?: string;
        product_img_show?: string;
        product_img_border_radius?: number;
        product_img_border_width?: number;
        product_img_border_color?: string;
        border_color: string;
        border_width: number;
        border_radius: number;
        background_color: string;
        highlighted_offer_tag_size: number;
        highlighted_offer_tag_color: string;
        highlighted_offer_tag_style: FontStylesTypes;
        highlighted_price_tag_size: number;
        highlighted_price_tag_color: string;
        highlighted_price_tag_style: FontStylesTypes;
        total_footer_text_size: number;
        total_footer_text_color: string;
        total_footer_text_style: FontStylesTypes;
        total_footer_price_text_size: number;
        total_footer_price_text_color: string;
        total_footer_price_text_style: FontStylesTypes;
        total_footer_background_color: string;
        total_footer_background_style: FontStylesTypes;
        product_background_color: string;
        product_border_color: string;
        add_to_cart_bg_color: string;
        add_to_cart_border_radius: number;
        add_to_cart_border_color: string;
        add_to_cart_border_width: number;
        add_to_cart_text_color: string;
        add_to_cart_text_size: number;
        add_to_cart_text_style: FontStylesTypes;
        swatches_select_color: string;
    };

    // content form
    bundle_name: string;
    title: string;
    description: string;
    fbt_product_selection: "all" | "manual";
    fbt_show_checkboxes: 1 | 0;
    ai_fbt_product_limit: number;
    show_ai_fbt_for: "every" | "include" | "exclude";
    ai_fbt_allowed_products: string[];
    discount_type: DiscountTypes;
    discount_value: number | null;
    products: ProductObj[];
    offered_products: ProductObj[];
    volume_discounts: any[];
    discounted_products: ProductObj[];
    bundle_product_level: "product" | "variant";
    add_to_cart_cta_text: string;
    footer_total_text: string;
    footer_text: string;
    free_text: string;
    order_discounts: boolean;
    product_discounts: boolean;
    shipping_discounts: boolean;
    start_date: string;
    end_date: string | null;
    use_compare_at_price: boolean;
    prevent_deselect_main_product: boolean;
    no_preselect_offered_products: boolean;
    allow_free_gifts?: boolean;
    free_gifts?: ProductObj[];
    free_gift_text?: string;
    show_swatches: boolean;
    selected_template_fbt: "vertical" | "horizontal";
    swatchOptions: SwatchOption[];
    swatchesProductsMap: SwatchesProductsMap;
    swatchShape: "circle" | "rounded" | "square";
    swatchSize: number;
    new_bundle_position: WidgetPositionsTypes;
    new_custom_selector: string;
    apply_discount_on_compare_price: boolean;
    offer_name: string;
    discount_label_template: string;

    // target form
    targeting: TargetingConfig;

    // Settings
    general_settings?: GeneralSettingObj;
    bundles_settings?: BundleTypeSettings;
    show_bundle_widget: WidgetShowOptionsTypes;
    free_shipping: boolean;
    animation_add_to_cart_btn: string;
    product_bundle_id?: string;
    max_width?: number;
    SwatchesBundleConfigTouchedRef?: any;
}

export interface FixedBoxConfig {
    itemsCount: number;
    fixedPrice: number;
    boxName: string;
}

export interface BundleBuilderConfigInterface {
    status: boolean;
    bundle_type?: string;
    is_paused?: boolean;
    form_id?: string;
    ai_bundle_status?: boolean;
    hide_out_of_stock_products: boolean;
    show_search_bar: boolean;
    search_bar_alignment_desktop?: "flex-start" | "center" | "flex-end";
    search_bar_alignment_mobile?: "flex-start" | "center" | "flex-end";
    auto_advance_when_condition_matched: boolean;
    hide_product_info_modal?: boolean;
    disable_product_title_link: boolean;
    hide_bundle_header: boolean;
    allow_quantity_input: boolean;
    secondary_button_type?: "edit_bundle" | "start_again";
    limit_max_quantity_per_product: boolean;
    max_quantity_per_product: number;
    footer_layout: "right" | "bottom";
    selected_template_bb:
        | "full_card_info"
        | "basic_card_info"
        | "img_focus_card"
        | "hover_action_card"
        | "hover_info_card";
    bundle_as_product: {
        product_title: string;
        product_description: string;
        show_on_product_page: string[];
        images: {
            id: string;
            originalSource: string;
            alt: string;
            mediaContentType?: "IMAGE";
        }[];
        product_handle: string;
        variant_id?: string;
        double_discount: boolean;
        collections: {
            id: string;
            image?: {
                originalSrc?: string;
            };
            title: string;
        }[];
    };
    render_embed_on_visibility?: boolean;
    isProductPageVisible: boolean;
    discount_options: BBTierOptions[];
    show_progress_bar?: boolean;
    no_products_selected_text?: string;
    file_upload_uploading_text?: string;
    file_upload_click_to_upload_multiple_text?: string;
    file_upload_click_to_upload_single_text?: string;
    file_upload_max_size_text?: string;
    file_upload_already_uploaded_error?: string;
    file_upload_only_one_file_error?: string;
    show_product_grid_view: boolean;
    show_broken_quantity_in_grid: boolean;
    // design form
    bundles_design: {
        default_cta: boolean;
        bundle_title_size: number;
        bundle_title_color: string;
        bundle_title_style: FontStylesTypes;
        bundle_widget_bg: string;
        bundle_widget_font_family: string;
        product_name_size: number;
        product_name_color: string;
        product_name_style: FontStylesTypes;
        product_price_size: number;
        product_price_color: string;
        product_price_style: FontStylesTypes;
        product_img_size?: string;
        is_product_img_height_auto?: boolean;
        product_custom_img_size?: number;
        product_custom_img_size_mobile?: number;
        product_img_show?: string;
        // product_img_border_radius?: number;
        // product_img_border_width?: number;
        // product_img_border_color?: string;
        // border_color: string;
        border_width: number;
        border_radius: number;
        // background_color: string;
        // highlighted_offer_tag_size: number;
        // highlighted_offer_tag_color: string;
        // highlighted_offer_tag_style: FontStylesTypes;
        highlighted_price_tag_size: number;
        highlighted_price_tag_color: string;
        highlighted_price_tag_style: FontStylesTypes;
        total_footer_text_size: number;
        total_footer_text_color: string;
        total_footer_price_color: string;
        total_footer_strike_off_price_color: string;
        total_footer_discount_color: string;
        total_footer_text_style: FontStylesTypes;
        // total_footer_price_text_size: number;
        // total_footer_price_text_color: string;
        // total_footer_price_text_style: FontStylesTypes;
        total_footer_background_color: string;
        // total_footer_background_style: FontStylesTypes;
        product_background_color: string;
        product_border_color: string;
        variant_selector_text_size: number;
        variant_selector_text_color: string;
        variant_selector_text_style: FontStylesTypes;
        add_to_cart_bg_color: string;
        edit_bundle_cta_bg_color: string;
        edit_bundle_cta_text_color: string;
        edit_bundle_cta_size: number;
        edit_bundle_cta_style: FontStylesTypes;
        add_to_cart_border_radius: number;
        // add_to_cart_border_color: string;
        // add_to_cart_border_width: number;
        add_to_cart_text_color: string;
        add_to_cart_text_size: number;
        add_to_cart_text_style: FontStylesTypes;
        // free_gift_header_bg_color: string;
        // free_gift_header_color: string;
        // free_gift_header_size: number;
        // free_gift_header_style: FontStylesTypes;
        // free_gift_bg_color: string;
        // free_gift_text_color: string;
        // free_gift_text_size: number;
        // free_gift_text_style: FontStylesTypes;
        // free_gift_border: boolean;
        // free_gift_border_width: number;
        // free_gift_border_color: string;
        fixed_boxes_bg_color: string;
        fixed_boxes_selected_bg_color: string;
        fixed_boxes_text_color: string;
        fixed_boxes_selected_text_color: string;
        fixed_boxes_text_size: number;
        secondary_cta_bg_color: string;
        secondary_cta_text_color: string;
        tertiary_cta_bg_color: string;
        tertiary_cta_text_color: string;
        review_product_border_color: string;
        review_product_border_width: number;
        review_product_border_radius: number;
        review_product_img_border_color: string;
        review_product_img_border_radius: number;
        review_product_img_border_width: number;
        step_label_color: string;
        step_page_title_color: string;
        step_page_title_size: number;
        step_page_title_style: FontStylesTypes;
        step_circle_bg_color: string;
        step_circle_color: string;

        step_page_subtitle_color: string;
        step_page_subtitle_size: number;
        step_page_subtitle_style: FontStylesTypes;
        order_summary_bg_color: string;
        order_summary_header_text_color: string;
        order_summary_header_text_size: number;
        order_summary_header_text_style: string;
        order_summary_rows_text_color: string;
        order_summary_rows_text_size: number;
        order_summary_rows_text_style: string;
        bb_margin_top: number;
        bb_margin_bottom: number;
        product_card_title_alignment: "start" | "end" | "left" | "right" | "center" | "justify" | "match-parent";
        substep_bg_color: string;
        substep_text_color: string;
        substep_header_text_size: number;
        mobile_substep_header_layout: "dropdown" | "tabs" | "scrollable_tabs";
        // popup_cta_bg_color: string;
        popup_bg_color: string;
        popup_text_color: string;

        progress_bar_color: string;
        progress_bar_bg_color: string;
        addon_title_text_color: string;
        addon_title_text_size: number;
        addon_card_bg_color: string;
    };
    order_discounts: boolean;
    product_discounts: boolean;
    shipping_discounts: boolean;
    // content form
    bundle_name: string;
    title: string;
    description: string;
    top_html: string;
    discount_type: DiscountTypes;
    tier_discount_basis: "items_count" | "bundle_total" | "product_based";
    discount_value: number;
    hide_prices_product_card: boolean;
    steps: StepsType[];
    default_step: StepsType;
    free_gift_step?: FreeGiftStepType;
    review_step: ReviewStepType;
    products: ProductObj[];
    bundle_product_level: "product" | "variant";
    hide_native_elements: string;
    add_to_cart_cta_text: string;
    quantity_text: string;
    footer_total_text: string;
    footer_text: string;
    free_text: string;
    item_added_text: string;
    buy_now_cta_text: string;
    bundle_ready_text?: string;
    edit_cta_text: string;
    start_again_cta_text: string;
    next_cta_text: string;
    previous_cta_text: string;
    checkout_cta_text: string;
    checkout_continue_shopping_cta_text?: string;
    checkout_view_cart_cta_text?: string;
    checkout_box_label: string;
    checkout_discount_label: string;
    show_more_text?: string;
    show_less_text?: string;
    out_of_stock_text: string;
    order_summary_title: string;
    order_summary_subtotal: string;
    order_summary_discount: string;
    empty_card_footer_text: string;

    start_date: string;
    end_date: string | null;
    use_compare_at_price: boolean;
    show_product_price_with_compare_price: boolean;
    allow_free_gifts: boolean;
    free_gifts: any[];
    free_gift_text: string;
    bundle_embed_id: string;
    show_swatches: boolean;
    swatchOptions: SwatchOption[];
    swatchesProductsMap: SwatchesProductsMap;
    swatchShape: "circle" | "rounded" | "square";
    swatchSize: number;
    offer_cta_text: string;
    limit_show_offer_cta: "all" | "products" | "collections" | "none";
    show_offer_cta_pages: ProductObj[];
    offer_cta_position: BbOfferCtaPositionsType;
    bundle_builder_link: string;
    allow_pre_selected_products: boolean;
    pre_selected_products: {
        title: string;
        quantity: number;
        id: string;
        image: {
            url: string;
        };
    }[];
    make_pre_selected_compulsory: boolean;
    product_card_desktop_size: "small" | "medium" | "large";
    product_card_mobile_size: "small" | "medium" | "large";
    allow_conditional_logic: boolean;
    product_conditions: ProductConditions[];
    redirect_options: "cart" | "checkout";
    show_popup_on_cart_redirect: boolean;
    popup_text: string;
    popup_redirect_url: string;
    apply_discount_on_compare_price: boolean;
    offer_name: string;
    b2b_discounts_enabled: boolean;
    b2b_discount_type: DiscountTypes;
    b2b_discount_value: number;
    fixed_boxes: FixedBoxConfig[];
    fixed_boxes_upsell_message: string;
    fixed_boxes_success_message: string;
    substep_layout?: "tabs" | "sequential";
    show_total_quantity_in_footer?: boolean;
    choose_option_text: string;
    disable_cta_when_stock_reached: boolean;

    // target form
    targeting: TargetingConfig;

    // Settings
    general_settings?: GeneralSettingObj;
    bundles_settings?: BundleTypeSettings;
    show_bundle_widget: WidgetShowOptionsTypes;
    free_shipping: boolean;
    animation_add_to_cart_btn: string;
    product_bundle_id?: string;
    max_width?: number;
    SwatchesBundleConfigTouchedRef?: any;
    allow_exclude_from_step_conditions?: boolean;

    // AB Testing
    ab_config?: ABTestingConfig<BundleBuilderConfigInterface>;
    // resolved by prerender.js when ab_config.enable === 1
    _ab_variant_id?: string;
}

export interface PlaceholderConfigInterface {
    status: boolean;
    bundle_type?: string;
    is_paused?: boolean;
    form_id?: string;
    ai_bundle_status?: boolean;
    hide_out_of_stock_products: boolean;
    show_search_bar: boolean;
    limit_max_quantity_per_product: boolean;
    max_quantity_per_product: number;
    move_selected_products_first?: boolean;
    accordion_product_layout?: "grid" | "scroll";
    bundle_as_product: {
        product_title: string;
        product_description: string;
        show_on_product_page: string[];
        images: {
            id: string;
            originalSource: string;
            alt: string;
            mediaContentType?: "IMAGE";
        }[];
        product_handle: string;
        variant_id?: string;
        double_discount: boolean;
        collections: {
            id: string;
            image?: {
                originalSrc?: string;
            };
            title: string;
        }[];
    };
    render_embed_on_visibility?: boolean;
    isProductPageVisible: boolean;
    // design form
    bundles_design: {
        default_cta: boolean;
        bundle_title_size: number;
        bundle_title_color: string;
        bundle_title_style: FontStylesTypes;
        bundle_widget_bg: string;
        bundle_widget_font_family: string;
        product_name_size: number;
        product_name_color: string;
        product_name_style: FontStylesTypes;
        product_price_size: number;
        product_price_color: string;
        product_price_style: FontStylesTypes;
        product_img_size?: string;
        is_product_img_height_auto?: boolean;
        product_custom_img_size?: number;
        product_custom_img_size_mobile?: number;
        product_img_show?: string;
        product_img_border_radius?: number;
        product_img_border_width?: number;
        product_img_border_color?: string;
        border_color: string;
        border_width: number;
        border_radius: number;
        background_color: string;
        highlighted_offer_tag_size: number;
        highlighted_offer_tag_color: string;
        highlighted_offer_tag_style: FontStylesTypes;
        highlighted_price_tag_size: number;
        highlighted_price_tag_color: string;
        highlighted_price_tag_style: FontStylesTypes;
        total_footer_text_size: number;
        total_footer_text_color: string;
        total_footer_text_style: FontStylesTypes;
        total_footer_price_text_size: number;
        total_footer_price_text_color: string;
        total_footer_price_text_style: FontStylesTypes;
        total_footer_background_color: string;
        total_footer_background_style: FontStylesTypes;
        product_background_color: string;
        product_border_color: string;
        variant_selector_text_size: number;
        variant_selector_text_color: string;
        variant_selector_text_style: FontStylesTypes;
        add_to_cart_bg_color: string;
        edit_bundle_cta_bg_color: string;
        edit_bundle_cta_text_color: string;
        edit_bundle_cta_size: number;
        edit_bundle_cta_style: FontStylesTypes;
        add_to_cart_border_radius: number;
        add_to_cart_border_color: string;
        add_to_cart_text_color: string;
        add_to_cart_text_size: number;
        add_to_cart_text_style: FontStylesTypes;
        free_gift_header_bg_color: string;
        free_gift_header_color: string;
        free_gift_header_size: number;
        free_gift_header_style: FontStylesTypes;
        free_gift_bg_color: string;
        free_gift_text_color: string;
        free_gift_text_size: number;
        free_gift_text_style: FontStylesTypes;
        free_gift_border: boolean;
        free_gift_border_width: number;
        free_gift_border_color: string;
        volume_product_selection?: string;
        secondary_cta_bg_color: string;
        secondary_cta_text_color: string;
        tertiary_cta_bg_color: string;
        tertiary_cta_text_color: string;
        step_label_color: string;
        step_label_style: string;
        step_label_size: number;
        step_page_title_color: string;
        step_page_title_size: number;
        step_page_title_style: FontStylesTypes;
        step_page_subtitle_color: string;
        step_page_subtitle_size: number;
        step_page_subtitle_style: FontStylesTypes;
        order_summary_bg_color: string;
        order_summary_header_text_color: string;
        order_summary_header_text_size: number;
        order_summary_header_text_style: string;
        order_summary_rows_text_color: string;
        order_summary_rows_text_size: number;
        order_summary_rows_text_style: string;
        bb_margin_top: number;
        popup_cta_bg_color: string;
        special_text_background_color: string;
        special_text_color: string;
        special_text_font_size: number;
        special_text_style: string;
        added_quantity_bg_color: string;
        added_quantity_text_color: string;
        added_quantity_text_style: string;
        added_quantity_text_size: number;
        modal_footer_bg: string;
        dotted_border_color: string;
        step_container_bg: string;
    };

    // content form
    bundle_name: string;
    title: string;
    description: string;
    discount_type: DiscountTypes;
    discount_value: number | null;
    tier_discount_basis: "items_count" | "bundle_total" | "product_based";
    discount_options: BBTierOptions[];
    show_progress_bar: boolean;
    hide_prices_product_card: boolean;
    steps: PlaceholderStepsType[];
    default_step: PlaceholderStepsType;
    free_gift_step?: FreeGiftStepType;
    products: ProductObj[];
    bundle_product_level: "product" | "variant";
    hide_native_elements: string;
    add_to_cart_cta_text: string;
    add_to_cart_cta_hover_text: string;
    quantity_text: string;
    footer_total_text: string;
    footer_text: string;
    free_text: string;
    buy_now_cta_text: string;
    bundle_ready_text?: string;
    edit_cta_text: string;
    next_cta_text: string;
    previous_cta_text: string;
    checkout_cta_text: string;
    done_cta_text: string;
    out_of_stock_text: string;
    order_summary_title: string;
    order_summary_subtotal: string;
    order_summary_discount: string;
    tooltip_message: string;
    start_date: string;
    end_date: string | null;
    use_compare_at_price: boolean;
    allow_free_gifts: boolean;
    free_gifts: any[];
    free_gift_text: string;
    bundle_embed_id: string;
    show_swatches: boolean;
    swatchOptions: SwatchOption[];
    swatchesProductsMap: SwatchesProductsMap;
    swatchShape: "circle" | "rounded" | "square";
    swatchSize: number;
    offer_cta_text: string;
    limit_show_offer_cta: "all" | "products" | "collections" | "none";
    show_offer_cta_pages: ProductObj[];
    offer_cta_position: BbOfferCtaPositionsType;
    allow_pre_selected_products: boolean;
    pre_selected_products: {
        title: string;
        quantity: number;
        id: string;
        image: {
            url: string;
        };
    }[];
    make_pre_selected_compulsory: boolean;
    product_card_desktop_size: "small" | "medium" | "large";
    product_card_mobile_size: "small" | "medium" | "large";
    allow_conditional_logic: boolean;
    product_conditions: {
        included_products: ProductObj[];
        hidden_products: ProductObj[];
    }[];
    redirect_options: "cart" | "checkout";
    show_popup_on_cart_redirect: boolean;
    popup_text: string;
    popup_redirect_url: string;
    order_discounts: boolean;
    product_discounts: boolean;
    shipping_discounts: boolean;
    apply_discount_on_compare_price: boolean;
    no_close_accordian_after_completion: boolean;
    offer_name: string;
    selected_template_placeholder: "normal" | "accordian";

    // target form
    targeting: TargetingConfig;

    // Settings
    general_settings?: GeneralSettingObj;
    bundles_settings?: BundleTypeSettings;
    show_bundle_widget: WidgetShowOptionsTypes;
    free_shipping: boolean;
    animation_add_to_cart_btn: string;
    product_bundle_id?: string;
    max_width?: number;
    SwatchesBundleConfigTouchedRef?: any;
}
export type BxgyDiscountedVariantMode = "customer_chooses" | "same_as_buy";

export interface BxgyConfigInterface {
    status: boolean;
    bundle_type?: string;
    is_paused?: boolean;
    render_embed_on_visibility: boolean;
    form_id?: string;
    ai_bundle_status?: boolean;
    show_product_quantity: boolean;
    // how the discounted item's variant is picked; missing on bundles created before this option existed
    discounted_variant_mode?: BxgyDiscountedVariantMode;
    bundle_as_product: {
        product_title: string;
        product_description: string;
        show_on_product_page: string[];
        images: {
            id: string;
            originalSource: string;
            alt: string;
            mediaContentType?: "IMAGE";
        }[];
        product_handle: string;
        variant_id?: string;
        double_discount: boolean;
        collections: {
            id: string;
            image?: {
                originalSrc?: string;
            };
            title: string;
        }[];
    };
    allowed_product_pages: string[] | [];
    render_product_pages_type: boolean;
    hide_native_elements: string;
    isProductPageVisible: boolean;
    selected_template_bxgy: string;
    // design form
    bundles_design: {
        default_cta: boolean;
        bundle_title_size: number;
        bundle_title_color: string;
        bundle_title_style: FontStylesTypes;
        bundle_widget_bg: string;
        bundle_widget_font_family: string;
        product_name_size: number;
        product_name_color: string;
        product_name_style: FontStylesTypes;
        product_price_size: number;
        product_price_color: string;
        product_price_style: FontStylesTypes;
        variant_selector_text_size: number;
        variant_selector_text_color: string;
        variant_selector_text_style: FontStylesTypes;
        border_color: string;
        border_width: number;
        border_radius: number;
        background_color: string;
        highlighted_offer_tag_size: number;
        highlighted_offer_tag_color: string;
        highlighted_offer_tag_style: FontStylesTypes;
        highlighted_price_tag_size: number;
        highlighted_price_tag_color: string;
        highlighted_price_tag_style: FontStylesTypes;
        total_footer_text_size: number;
        total_footer_text_color: string;
        total_footer_text_style: FontStylesTypes;
        total_footer_price_text_size: number;
        total_footer_price_text_color: string;
        total_footer_price_text_style: FontStylesTypes;
        total_footer_background_color: string;
        total_footer_background_style: FontStylesTypes;
        product_background_color: string;
        product_border_color: string;
        product_img_size?: string;
        product_img_show?: string;
        product_img_border_radius?: number;
        product_img_border_width?: number;
        product_img_border_color?: string;
        add_to_cart_bg_color: string;
        add_to_cart_border_radius: number;
        add_to_cart_border_color: string;
        add_to_cart_border_width: number;
        add_to_cart_text_color: string;
        add_to_cart_text_size: number;
        add_to_cart_text_style: FontStylesTypes;
        special_text_background_color: string;
        special_text_color: string;
        special_text_font_size: number;
        special_text_style: string;

        free_gift_header_bg_color: string;
        free_gift_header_color: string;
        free_gift_header_size: number;
        free_gift_header_style: FontStylesTypes;
        free_gift_bg_color: string;
        free_gift_text_color: string;
        free_gift_text_size: number;
        free_gift_text_style: FontStylesTypes;
        free_gift_border: boolean;
        free_gift_border_width: number;
        free_gift_border_color: string;

        footer_save_money_text_size: number;
        footer_save_money_text_style: string;
        footer_save_money_text_color: string;
        swatches_select_color: string;
    };

    // content form
    bundle_name: string;
    title: string;
    description: string;
    discount_type: DiscountTypes;
    discount_value: number | null;
    products: ProductObj[];
    offered_products: ProductObj[];
    volume_discounts: any[];
    discounted_products: ProductObj[];
    bundle_product_level: "product" | "variant";
    add_to_cart_cta_text: string;
    y_label_get_text: string;
    y_label_off_text: string;
    x_label_buy_text: string;

    quantity_text: string;
    footer_total_text: string;
    free_text: string;

    order_discounts: boolean;
    product_discounts: boolean;
    shipping_discounts: boolean;
    start_date: string;
    end_date: string | null;
    use_compare_at_price: boolean;
    allow_free_gifts: boolean;
    free_gifts: [];
    free_gift_text: string;
    bundle_embed_id: string;
    show_swatches: boolean;
    swatchOptions: SwatchOption[];
    swatchesProductsMap: SwatchesProductsMap;
    swatchShape: "circle" | "rounded" | "square";
    swatchSize: number;
    offer_name: string;
    discount_label_template: string;
    should_flatten_products: boolean;

    // target form
    targeting: TargetingConfig;

    // Settings
    general_settings?: GeneralSettingObj;
    bundles_settings?: BundleTypeSettings;
    show_bundle_widget: WidgetShowOptionsTypes;
    free_shipping: boolean;
    animation_add_to_cart_btn: string;
    product_bundle_id?: string;
    max_width?: number;
    SwatchesBundleConfigTouchedRef?: any;
}

// -------------------------------------- Volume Bundle Specific

export interface VolumeBundleSettings {
    enable_stock_control: boolean;
    out_of_stock_text: string;
    listing_variant_with_image: boolean;
    work_only_with_widget: boolean;
    bundle_position: WidgetPositionsTypes;
}

export interface OfferInterface {
    discount_type: DiscountTypes;
    discount_value: number;
    quantity: number;
    offer_title: string;
    discount_label: string;
    discount_label_selected: boolean;
    discount_label_type: "none" | "custom" | "ribbon" | "ticket" | "tag" | "skull" | "flag";
    discount_label_custom_img: string;
    discount_label_custom_img_size: number;
    discount_label_custom_img_radius: number;
    discount_label_custom_img_fit: "cover" | "contain" | "fill";
    tag: string;
    tag_selected: boolean;
    allow_free_gifts: boolean;
    free_gifts: ProductObj[];
    free_gift_text: string;
    allow_img: boolean;
    img_url: string;
    img_size: number;
    img_radius: number;
    img_fit: "cover" | "contain" | "fill";
    allow_extra_items?: boolean;
    show_quantity_selector: boolean;
    free_gift_template: "old" | "modern"; // old allow multiple FGs and modern allow single FG
    free_gift_show_price?: boolean;
    offer_type: "quantity_break" | "product_bundle";
    bundle_products: ProductObj[];
    bundle_layout: "vertical" | "horizontal";
    allow_upsell_products: boolean;
    upsell_products: ProductObj[];
}

export interface ErrorInterface {
    discount_type: string;
    discount_value: string;
    quantity: string;
    offer_title: string;
    discount_label: string;
    discount_label_selected: string;
    tag: string;
    tag_selected: string;
    // error type
    free_gifts: string;
    free_gift_text: string;
    img_url: string;
    upsell_products?: string;
}
export interface VolumeConfigInterface {
    volume_discounts: OfferInterface[];
    status: boolean;
    enable_subscription_integration: boolean;
    bundle_type?: string;
    is_paused?: boolean;
    form_id?: string;
    collections: {
        id: string;
        image?: {
            originalSrc?: string;
        };
        title: string;
    }[];
    ai_bundle_status?: boolean;
    // design form
    bundles_design: {
        default_cta: boolean;
        bundle_title_size: number;
        bundle_title_color: string;
        bundle_title_style: FontStylesTypes;
        bundle_widget_bg: string;
        bundle_widget_font_family: string;
        product_name_size: number;
        product_name_color: string;
        product_name_style: FontStylesTypes;
        product_price_size: number;
        product_price_color: string;
        product_price_style: FontStylesTypes;
        variant_selector_text_size: number;
        variant_selector_text_color: string;
        variant_selector_text_style: FontStylesTypes;
        product_img_size?: string;
        product_img_show?: string;
        product_img_border_radius?: number;
        border_color: string;
        border_width: number;
        border_radius: number;
        bundle_border_radius: number;
        background_color: string;
        hover_effect: "none" | "box_shadow" | "lift" | "scale" | "border_highlight" | "pulse";
        hover_color: string;
        hover_intensity: number;
        hover_size: number;
        elegant_badge_content: string;
        highlighted_offer_tag_size: number;
        highlighted_offer_tag_color: string;
        highlighted_offer_tag_background_color: string;
        highlighted_offer_tag_style: FontStylesTypes;
        highlighted_price_tag_size: number;
        highlighted_price_tag_color: string;
        highlighted_price_tag_style: FontStylesTypes;
        total_footer_text_size: number;
        total_footer_text_color: string;
        total_footer_text_style: FontStylesTypes;
        total_footer_price_text_size: number;
        total_footer_price_text_color: string;
        total_footer_price_text_style: FontStylesTypes;
        total_footer_background_color: string;
        total_footer_background_style: FontStylesTypes;
        product_background_color: string;
        product_border_color: string;
        add_to_cart_bg_color: string;
        add_to_cart_hover_bg_color: string;
        add_to_cart_border_radius: number;
        add_to_cart_border_color: string;
        add_to_cart_border_width: number;
        add_to_cart_text_color: string;
        add_to_cart_text_size: number;
        add_to_cart_text_style: FontStylesTypes;
        free_gift_header_bg_color: string;
        free_gift_header_color: string;
        free_gift_header_size: number;
        free_gift_header_style: FontStylesTypes;
        free_gift_bg_color: string;
        free_gift_text_color: string;
        free_gift_text_size: number;
        free_gift_text_style: FontStylesTypes;
        free_gift_border: boolean;
        free_gift_border_width: number;
        free_gift_border_color: string;
        free_gift_compare_price_color: string;
        free_gift_compare_price_size: number;
        free_gift_compare_price_style: FontStylesTypes;
        upsell_bg_color: string;
        upsell_text_color: string;
        upsell_text_size: number;
        upsell_text_style: FontStylesTypes;
        upsell_price_color: string;
        upsell_price_size: number;
        upsell_price_style: FontStylesTypes;
        upsell_compare_price_color: string;
        upsell_compare_price_size: number;
        upsell_compare_price_style: FontStylesTypes;
        quantity_selector_bg_color: string;
        quantity_selector_border_color: string;
        quantity_selector_border_width: number;
        quantity_selector_border_radius: number;
        quantity_selector_text_color: string;
        quantity_selector_text_size: number;
        quantity_selector_text_style: FontStylesTypes;
        special_text_background_color: string;
        special_text_color: string;
        special_text_font_size: number;
        special_text_style: string;
        discount_label_background_color: string;
        discount_label_color: string;
        discount_label_font_size: number;
        discount_label_style: string;
        discount_label_primary_color: string;
        discount_label_secondary_color: string;
        discount_label_size: number;
        special_text_bg_opacity: boolean;
        swatches_select_color: string;
        show_line_around_title_color: string;
        timer_color: string;
        timer_text_color: string;
        timer_background_color: string;
        timer_font_style: FontStylesTypes;
        timer_font_size: number;
        savings_summary_text_color: string;
        savings_summary_background_color: string;
        savings_summary_font_size: number;
        savings_summary_font_style: FontStylesTypes;
        savings_summary_border_radius: number;
    };
    volume_product_selection: "all" | "specific" | "exclude" | "collection";
    bundle_visibility: BundleVisibilityTypes;
    // content form
    bundle_name: string;
    default_offer: number;
    title: string;
    description: string;
    products: ProductObj[];
    offered_products?: ProductObj[];
    hide_native_elements: string;
    discounted_products: ProductObj[];
    bundle_product_level: "product" | "variant";
    add_to_cart_cta_text: string;
    add_to_cart_cta_icon_type: "none" | "checkmark" | "dot" | "arrow" | "star" | "heart" | "thumbs_up" | "custom";
    add_to_cart_cta_icon_url: string;
    savings_summary: {
        status: boolean;
        title: string;
        icon_type: "none" | "checkmark" | "dot" | "arrow" | "star" | "heart" | "thumbs_up" | "custom";
        icon_url: string;
        include_free_gift_value: boolean;
        hide_when_no_savings: boolean;
        text_align: "left" | "center" | "right";
    };
    quantity_text: string;
    free_text: string;
    order_discounts: boolean;
    product_discounts: boolean;
    shipping_discounts: boolean;
    start_date: string;
    end_date: string | null;
    show_unit_price: boolean;
    show_free_gift_popper: boolean;
    use_compare_at_price: boolean;
    selected_template_volume: string;
    show_variant_selector: boolean;
    show_swatches: boolean;
    show_option_labels_once: boolean;
    hide_item_numbers: boolean;
    use_compact_spacing: boolean;
    variant_label_position: "above" | "beside";
    show_line_around_title: boolean;
    show_unit_price_text: string;
    swatchOptions: SwatchOption[];
    swatchesProductsMap: SwatchesProductsMap;
    swatchShape: "circle" | "rounded" | "square";
    swatchSize: number;
    apply_discount_on_compare_price: boolean;

    show_countdown_timer: boolean;
    countdown_timer_minutes: number;
    countdown_timer_text: string;
    out_of_stock_disable_text: string;
    no_stock_text: string;
    offer_name: string;

    // AB Testing
    ab_config: ABTestingConfig<VolumeConfigInterface>;
    // resolved by prerender.js when ab_config.enable === 1
    _ab_variant_id?: string;

    // target form
    targeting: TargetingConfig;

    // Settings
    general_settings?: GeneralSettingObj;
    bundles_settings?: BundleTypeSettings;
    show_bundle_widget: WidgetShowOptionsTypes;
    free_shipping: boolean;
    animation_add_to_cart_btn: string;
    product_bundle_id?: string;
    max_width?: number;
    SwatchesBundleConfigTouchedRef?: any;
    defaultVariantTypes: DefaultVariantType[];

    // required for manual sending some additional properties
    free_gifts: any[];
    bundle_products: any[];
    upsell_products: any[];

    // Being used on store side
    free_gift_text?: string;
    free_gift_template?: string;
    free_gift_show_price?: boolean;
    shop?: string;

    // being used on admin side only
    source?: "defaultVariantModal";
    created_at?: string;
    updated_at?: string;
}

// Shape returned by the lightweight get-volume-bundle-designs endpoint —
// just enough to offer "reuse my last volume bundle's design".
export interface VolumeBundleDesignSummary {
    form_id?: string;
    bundle_type?: string;
    title?: string;
    status?: boolean;
    created_at?: string;
    updated_at?: string;
    bundles_design?: VolumeConfigInterface["bundles_design"];
}

// ----------------------------------------Mix and Match config------------

export interface MixMatchOption {
    id: string;
    discountType: DiscountTypes;
    discountValue: number;
    quantity: number;
    maxQuantity: number;
    addRange: boolean | null;
    allowExtraItems?: boolean;
    allow_free_gifts: boolean;
    free_gifts: ProductObj[];
    free_gift_text: string;
    max_discount_allowed: number | null;
    finalSuccessMessage?: string;
    showFinalSuccessMessage?: boolean;
    TierSuccessMessage?: string;
    ShowTierSuccessMessage?: boolean;
}

export interface BuyNOptions {
    required_item_count: number;
    discounted_item_count: number;
    discount_type: "percentage" | "flat" | "free";
    discount_value: number;
    label_text: string;
}

export interface BBTierOptions {
    id: string;
    discountType: DiscountTypes;
    discountValue: number;
    quantity: number;
    maxQuantity: number;
    addRange: boolean | null;
    finalSuccessMessage?: string; // present only in the first item
    showFinalSuccessMessage?: boolean;
    TierSuccessMessage: string;
    ShowTierSuccessMessage: boolean;
    allowExtraItems?: boolean;
    selected_products?: ProductObj[];
}
export interface DiscountErrorInterface {
    quantity?: string;
    discountValue?: string;
    discountType?: string;
    maxQuantity?: string;
    free_gifts?: string;
    free_gift_text?: string;
    max_discount_allowed?: string;
    selected_products?: string;
}
export interface SectionsErrorInterface {
    title?: string;
    subtitle?: string;
    products?: string;
    collections?: string;
}

export interface MixMatchConfigInterface {
    status: boolean;
    bundle_type?: string;
    is_paused?: boolean;
    form_id?: string;
    ai_bundle_status?: boolean;
    limit_max_quantity_per_product: boolean;
    max_quantity_per_product: number;
    render_embed_on_visibility: boolean;
    hide_native_elements: string;
    collections: {
        id: string;
        image?: {
            originalSrc?: string;
        };
        title: string;
    }[];
    show_progress_bar?: boolean;
    bundle_as_product: {
        product_title: string;
        product_description: string;
        show_on_product_page: string[];
        images: {
            id: string;
            originalSource: string;
            alt: string;
            mediaContentType?: "IMAGE";
        }[];
        product_handle: string;
        variant_id?: string;
        double_discount: boolean;
        collections: {
            id: string;
            image?: {
                originalSrc?: string;
            };
            title: string;
        }[];
    };
    isProductPageVisible: boolean;
    // design form
    bundles_design: {
        default_cta: boolean;
        bundle_title_size: number;
        bundle_title_color: string;
        bundle_title_style: FontStylesTypes;
        bundle_widget_bg: string;
        bundle_widget_font_family: string;
        product_name_size: number;
        product_name_color: string;
        product_name_style: FontStylesTypes;
        product_price_size: number;
        product_price_color: string;
        product_price_style: FontStylesTypes;
        product_img_size?: string;
        product_img_show?: string;
        product_img_border_radius?: number;
        product_img_border_width?: number;
        product_img_border_color?: string;

        border_color: string;
        border_width: number;
        border_radius: number;
        background_color: string;
        highlighted_offer_tag_size: number;
        highlighted_offer_tag_color: string;
        highlighted_offer_tag_style: FontStylesTypes;
        highlighted_price_tag_size: number;
        highlighted_price_tag_color: string;
        highlighted_price_tag_style: FontStylesTypes;
        total_footer_text_size: number;
        total_footer_text_color: string;
        total_footer_text_style: FontStylesTypes;
        total_footer_price_text_size: number;
        total_footer_price_text_color: string;
        total_footer_price_text_style: FontStylesTypes;
        total_footer_background_color: string;
        total_footer_background_style: FontStylesTypes;
        product_background_color: string;
        product_border_color: string;
        variant_selector_text_size: number;
        variant_selector_text_color: string;
        variant_selector_text_style: FontStylesTypes;
        add_to_cart_bg_color: string;
        add_to_cart_border_radius: number;
        add_to_cart_border_color: string;
        add_to_cart_border_width: number;
        add_to_cart_text_color: string;
        add_to_cart_text_size: number;
        add_to_cart_text_style: FontStylesTypes;
        add_button_bg_color: string;
        add_button_text_color: string;
        add_button_text_size: number;
        add_button_text_style: FontStylesTypes;
        add_button_border_color: string;
        add_button_border_width: number;
        add_button_border_radius: number;
        progress_bar_color: string;
        progress_bar_bg_color: string;
        discount_tabs_quantity_label_style: FontStylesTypes;
        discount_tabs_quantity_label_size: number;
        discount_tabs_quantity_label_color: string;
        discount_tabs_discount_label_size: number;
        discount_tabs_discount_label_color: string;
        discount_tabs_bg_color: string;
        discount_tabs_border_radius: number;
        free_gift_header_bg_color: string;
        free_gift_header_color: string;
        free_gift_header_size: number;
        free_gift_header_style: FontStylesTypes;
        free_gift_bg_color: string;
        free_gift_text_color: string;
        free_gift_text_size: number;
        free_gift_text_style: FontStylesTypes;
        free_gift_border: boolean;
        free_gift_border_width: number;
        free_gift_border_color: string;
        swatches_select_color: string;
        discount_tabs_selected_bg_color: string;
        discount_tabs_selected_text_color: string;
        discount_tabs_border_width: number;
        discount_tabs_border_color: string;
        discount_tabs_selected_border_color: string;
    };
    allowed_product_pages: string[] | [];
    render_product_pages_type: boolean;
    allowed_collection_product_pages?: ProductObj[];
    render_on_selected_collection_products?: boolean;
    show_on_no_pages?: boolean;
    // content form
    bundle_name: string;
    title: string;
    description: string;
    discount_options: MixMatchOption[];
    products: ProductObj[];
    offered_products: ProductObj[];
    volume_discounts: any[];
    discounted_products: ProductObj[];
    bundle_product_level: "product" | "variant";
    add_to_cart_cta_text: string;
    add_button: string;
    offer_free_gift_text: string;
    quantity_text: string;
    footer_total_text: string;
    footer_text: string;
    free_text: string;
    option_items_text: string;
    option_off_text: string;
    option_upto_text: string;
    order_discounts: boolean;
    product_discounts: boolean;
    shipping_discounts: boolean;
    start_date: string;
    end_date: string | null;
    bundle_embed_id: string;
    render_as_variants: boolean;
    show_swatches: boolean;
    swatchOptions: SwatchOption[];
    swatchesProductsMap: SwatchesProductsMap;
    swatchShape: "circle" | "rounded" | "square";
    swatchSize: number;
    mixmatch_product_selection: "all" | "specific" | "collection";
    apply_discount_on_compare_price: boolean;
    show_prices: boolean;
    price_display_type?: "both_price_and_compare_at_price" | "price_only";
    offer_name: string;
    hide_out_of_stock_products: boolean;
    load_more_products_text: string;
    disable_atc_until_threshold?: boolean;
    atc_threshold?: number;
    atc_tooltip_text?: string;
    no_stock_text?: string;

    // target form
    targeting: TargetingConfig;

    // Settings
    general_settings?: GeneralSettingObj;
    bundles_settings?: BundleTypeSettings;
    show_bundle_widget: WidgetShowOptionsTypes;
    free_shipping: boolean;
    animation_add_to_cart_btn: string;
    max_width?: number;
    SwatchesBundleConfigTouchedRef?: any;
}

export interface BuyNConfigInterface {
    status: boolean;
    bundle_type?: string;
    is_paused?: boolean;
    form_id?: string;
    ai_bundle_status?: boolean;
    render_embed_on_visibility: boolean;
    hide_native_elements: string;
    collections: {
        id: string;
        image?: {
            originalSrc?: string;
        };
        title: string;
    }[];
    // design form
    bundles_design: {
        default_cta: boolean;
        bundle_title_size: number;
        bundle_title_color: string;
        bundle_title_style: FontStylesTypes;
        bundle_widget_bg: string;
        bundle_widget_font_family: string;
        product_name_size: number;
        product_name_color: string;
        product_name_style: FontStylesTypes;
        product_price_size: number;
        product_price_color: string;
        product_price_style: FontStylesTypes;
        product_img_size?: string;
        product_img_show?: string;
        product_img_border_radius?: number;
        product_img_border_width?: number;
        product_img_border_color?: string;

        border_color: string;
        border_width: number;
        border_radius: number;
        background_color: string;
        highlighted_offer_tag_size: number;
        highlighted_offer_tag_color: string;
        highlighted_offer_tag_style: FontStylesTypes;
        highlighted_price_tag_size: number;
        highlighted_price_tag_color: string;
        highlighted_price_tag_style: FontStylesTypes;
        total_footer_text_size: number;
        total_footer_text_color: string;
        total_footer_text_style: FontStylesTypes;
        total_footer_price_text_size: number;
        total_footer_price_text_color: string;
        total_footer_price_text_style: FontStylesTypes;
        total_footer_background_color: string;
        total_footer_background_style: FontStylesTypes;
        product_background_color: string;
        product_border_color: string;
        variant_selector_text_size: number;
        variant_selector_text_color: string;
        variant_selector_text_style: FontStylesTypes;
        add_to_cart_bg_color: string;
        add_to_cart_border_radius: number;
        add_to_cart_border_color: string;
        add_to_cart_border_width: number;
        add_to_cart_text_color: string;
        add_to_cart_text_size: number;
        add_to_cart_text_style: FontStylesTypes;
        discount_tabs_quantity_label_style: FontStylesTypes;
        discount_tabs_quantity_label_size: number;
        discount_tabs_quantity_label_color: string;
        discount_tabs_discount_label_size: number;
        discount_tabs_discount_label_color: string;
        discount_tabs_bg_color: string;
        discount_tabs_border_radius: number;
        swatches_select_color: string;
    };
    allowed_product_pages: string[] | [];
    render_product_pages_type: boolean;
    show_on_no_pages?: boolean;
    // content form
    bundle_name: string;
    title: string;
    description: string;
    discount_option_type: "cheapest" | "expensive";
    use_option_label_in_cart?: boolean;
    discount_options: BuyNOptions[];
    products: ProductObj[];
    offered_products: ProductObj[];
    volume_discounts: any[];
    discounted_products: ProductObj[];
    bundle_product_level: "product" | "variant";
    add_to_cart_cta_text: string;
    add_button: string;
    offer_free_gift_text: string;
    quantity_text: string;
    footer_total_text: string;
    footer_text: string;
    free_text: string;
    option_items_text: string;
    option_off_text: string;
    option_upto_text: string;
    order_discounts: boolean;
    product_discounts: boolean;
    shipping_discounts: boolean;
    start_date: string;
    end_date: string | null;
    bundle_embed_id: string;
    show_swatches: boolean;
    swatchOptions: SwatchOption[];
    swatchesProductsMap: SwatchesProductsMap;
    swatchShape: "circle" | "rounded" | "square";
    swatchSize: number;
    product_selection_type: "all" | "specific" | "collection";
    offer_name: string;

    // target form
    targeting: TargetingConfig;

    // Settings
    general_settings?: GeneralSettingObj;
    bundles_settings?: BundleTypeSettings;
    show_bundle_widget: WidgetShowOptionsTypes;
    free_shipping: boolean;
    animation_add_to_cart_btn: string;
    max_width?: number;
    SwatchesBundleConfigTouchedRef?: any;
}

export interface mix_match_section {
    id: string;
    title: string;
    subtitle: string;
    products?: ProductObj[];
    product_selection: "specific" | "collection";
    collections?: {
        id: string;
        image?: {
            originalSrc?: string;
        };
        title: string;
    }[]; // collection objects
}

export interface custom_field_option {
    label: string;
    value: string;
}
export interface custom_field {
    type: "textarea" | "radio"; // textarea or radio buttons
    title: string; // field label
    placeholder?: string; // for textarea
    options?: custom_field_option[]; // for radio
}

export interface MixMatchSectionalConfigInterface {
    status: boolean;
    bundle_type?: string;
    is_paused?: boolean;
    form_id?: string;
    ai_bundle_status?: boolean;
    render_embed_on_visibility: boolean;
    hide_native_elements: string;
    bundle_as_product: {
        product_title: string;
        product_description: string;
        show_on_product_page: string[];
        images: {
            id: string;
            originalSource: string;
            alt: string;
            mediaContentType?: "IMAGE";
        }[];
        product_handle: string;
        variant_id?: string;
        double_discount: boolean;
        collections: {
            id: string;
            image?: {
                originalSrc?: string;
            };
            title: string;
        }[];
    };
    isCustomFieldVisible: boolean;
    isProductPageVisible: boolean;
    collections: {
        id: string;
        image?: {
            originalSrc?: string;
        };
        title: string;
    }[];
    show_progress_bar?: boolean;
    // design form
    bundles_design: {
        default_cta: boolean;
        bundle_title_size: number;
        bundle_title_color: string;
        bundle_title_style: FontStylesTypes;
        bundle_widget_bg: string;
        bundle_widget_font_family: string;
        product_name_size: number;
        product_name_color: string;
        product_name_style: FontStylesTypes;
        product_price_size: number;
        product_price_color: string;
        product_price_style: FontStylesTypes;
        product_img_size?: string;
        product_img_show?: string;
        product_img_border_radius?: number;
        product_img_border_width?: number;
        product_img_border_color?: string;

        border_color: string;
        border_width: number;
        border_radius: number;
        background_color: string;
        highlighted_offer_tag_size: number;
        highlighted_offer_tag_color: string;
        highlighted_offer_tag_style: FontStylesTypes;
        highlighted_price_tag_size: number;
        highlighted_price_tag_color: string;
        highlighted_price_tag_style: FontStylesTypes;
        total_footer_text_size: number;
        total_footer_text_color: string;
        total_footer_text_style: FontStylesTypes;
        total_footer_price_text_size: number;
        total_footer_price_text_color: string;
        total_footer_price_text_style: FontStylesTypes;
        total_footer_background_color: string;
        total_footer_background_style: FontStylesTypes;
        product_background_color: string;
        product_border_color: string;
        variant_selector_text_size: number;
        variant_selector_text_color: string;
        variant_selector_text_style: FontStylesTypes;
        add_to_cart_bg_color: string;
        add_to_cart_border_radius: number;
        add_to_cart_border_color: string;
        add_to_cart_border_width: number;
        add_to_cart_text_color: string;
        add_to_cart_text_size: number;
        add_to_cart_text_style: FontStylesTypes;
        add_button_bg_color: string;
        add_button_text_color: string;
        add_button_text_size: number;
        add_button_text_style: FontStylesTypes;
        add_button_border_color: string;
        add_button_border_width: number;
        add_button_border_radius: number;
        progress_bar_color: string;
        progress_bar_bg_color: string;
        discount_tabs_quantity_label_style: FontStylesTypes;
        discount_tabs_quantity_label_size: number;
        discount_tabs_quantity_label_color: string;
        discount_tabs_discount_label_size: number;
        discount_tabs_discount_label_color: string;
        discount_tabs_bg_color: string;
        discount_tabs_border_radius: number;
        free_gift_header_bg_color: string;
        free_gift_header_color: string;
        free_gift_header_size: number;
        free_gift_header_style: FontStylesTypes;
        free_gift_bg_color: string;
        free_gift_text_color: string;
        free_gift_text_size: number;
        free_gift_text_style: FontStylesTypes;
        free_gift_border: boolean;
        free_gift_border_width: number;
        free_gift_border_color: string;

        section_tabs_title_style: FontStylesTypes;
        section_tabs_title_size: number;

        section_tabs_subtitle_size: number;
        section_tabs_subtitle_style: FontStylesTypes;
        section_tabs_title_color: string;
        section_tabs_subtitle_color: string;
        section_tabs_bg_color: string;
        section_tabs_border_radius: number;
        custom_field_title_color: string;
        custom_field_bg_color: string;
        custom_field_message_color: string;
        swatches_select_color: string;
        section_tabs_unselected_bg_color: string;
        section_tabs_unselected_title_color: string;
        discount_tabs_selected_bg_color: string;
        discount_tabs_selected_text_color: string;
        discount_tabs_border_width: number;
        discount_tabs_border_color: string;
        discount_tabs_selected_border_color: string;
    };
    mix_match_sections: mix_match_section[];
    custom_fields: custom_field;
    allowed_product_pages: string[] | [];
    render_product_pages_type: boolean;
    allowed_collection_product_pages?: ProductObj[];
    render_on_selected_collection_products?: boolean;
    show_on_no_pages?: boolean;
    // content form
    bundle_name: string;
    title: string;
    description: string;
    discount_options: MixMatchOption[];
    tier_discount_basis: "items_count" | "bundle_total";
    products: ProductObj[];
    offered_products: ProductObj[];
    volume_discounts: any[];
    discounted_products: ProductObj[];
    bundle_product_level: "product" | "variant";
    add_to_cart_cta_text: string;
    add_button: string;
    offer_free_gift_text: string;
    quantity_text: string;
    footer_total_text: string;
    footer_text: string;
    free_text: string;
    option_items_text: string;
    option_off_text: string;
    option_upto_text: string;
    order_discounts: boolean;
    product_discounts: boolean;
    shipping_discounts: boolean;
    start_date: string;
    end_date: string | null;
    bundle_embed_id: string;
    show_swatches: boolean;
    swatchOptions: SwatchOption[];
    swatchesProductsMap: SwatchesProductsMap;
    swatchShape: "circle" | "rounded" | "square";
    swatchSize: number;
    mixmatch_product_selection: "all" | "specific" | "collection";
    allow_bundle_condition: boolean;
    bundle_condition_type: "max_products" | "min_products" | "exact_products";
    product_count_limit: number;
    tooltip_error_message: string;
    allow_pre_selected_products: boolean;
    pre_selected_products: ProductObj[];
    apply_discount_on_compare_price: boolean;
    show_prices: boolean;
    price_display_type?: "both_price_and_compare_at_price" | "price_only";
    offer_name: string;
    no_stock_text: string;

    // target form
    targeting: TargetingConfig;

    // Settings
    general_settings?: GeneralSettingObj;
    bundles_settings?: BundleTypeSettings;
    show_bundle_widget: WidgetShowOptionsTypes;
    free_shipping: boolean;
    animation_add_to_cart_btn: string;
    max_width?: number;
    SwatchesBundleConfigTouchedRef?: any;
}

// ----------------------------------------General--------------------------

export type BundleConfig =
    | StandardConfigInterface
    | VolumeConfigInterface
    | MixMatchConfigInterface
    | FBTConfigInterface
    | BxgyConfigInterface
    | BundleBuilderConfigInterface
    | ProductAddonsConfigInterface;

export const ChangeEvents = {
    initState: "initState",
};

export interface Action<T> {
    type?: string;
    payload?: string | number | RangeSliderValue | object | boolean | null;
    init?: T;
    key?: keyof T;
    isComplex?: boolean;
    isArray?: boolean;
    index?: number;
    subKey?: string;
}

export type CustomEventData = {
    detail: {
        selected_options: Array<any>;
        not_found: Array<any>;
    };
};

export type CombinedBundleConfigInterface =
    | StandardConfigInterface
    | VolumeConfigInterface
    | BxgyConfigInterface
    | MixMatchConfigInterface
    | MixMatchSectionalConfigInterface
    | FBTConfigInterface
    | ProductAddonsConfigInterface
    | ProductAddonsPopupConfigInterface
    | BundleBuilderConfigInterface
    | PlaceholderConfigInterface
    | BuyNConfigInterface;

export type AppPlanDiscount = {
    value: number;
    type: string;
    recurringTime: number;
    totalPriceToBe?: number;
};
